import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Patient, Medication, Doctor } from "@/types";
import { PrescriptionHeader } from "./PrescriptionHeader";
import { ComplaintEntry } from "./ComplaintEntry";
import { ClinicalDetails } from "./ClinicalDetails";
import { MedicationForm } from "./MedicationForm";
import { AdditionalNotes } from "./AdditionalNotes";
import { PrescriptionActions } from "./PrescriptionActions";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";

interface PrescriptionFormProps {
  patient: Patient;
  doctorName: string;
  chiefComplaints: string;
  setChiefComplaints: (complaints: string) => void;
  complaintsDuration: string;
  setComplaintsDuration: (duration: string) => void;
  clinicalFindings: string;
  setClinicalFindings: (findings: string) => void;
  diagnosis: string;
  setDiagnosis: (diagnosis: string) => void;
  labTests: string;
  setLabTests: (tests: string) => void;
  medications: Medication[];
  handleAddMedication: (medication: Medication) => void;
  handleAddMultipleMedications?: (medications: Medication[]) => void;
  handleRemoveMedication: (index: number) => void;
  handleUpdateMedication?: (medication: Medication, index: number) => void;
  notes: string;
  setNotes: (notes: string) => void;
  followUpDays: number;
  setFollowUpDays: (days: number) => void;
  handleSavePrescription: (status: "draft" | "issued") => void;
  existingDraft?: boolean;
}

export function PrescriptionForm({
  patient,
  doctorName,
  chiefComplaints,
  setChiefComplaints,
  complaintsDuration,
  setComplaintsDuration,
  clinicalFindings,
  setClinicalFindings,
  diagnosis,
  setDiagnosis,
  labTests,
  setLabTests,
  medications,
  handleAddMedication,
  handleAddMultipleMedications,
  handleRemoveMedication,
  handleUpdateMedication,
  notes,
  setNotes,
  followUpDays,
  setFollowUpDays,
  handleSavePrescription,
  existingDraft
}: PrescriptionFormProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{existingDraft ? "Edit Draft Prescription" : "New Prescription"}</CardTitle>
        <CardDescription>
          {existingDraft 
            ? `Continue editing the draft prescription for ${patient.name}`
            : `Create an electronic prescription for ${patient.name}`
          }
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <PrescriptionHeader 
          patient={patient} 
          doctorName={doctorName} 
        />
        
        <Separator />
        
        {/* Chief Complaints Section */}
        <ComplaintEntry 
          complaints={chiefComplaints}
          duration={complaintsDuration}
          onComplaintsChange={setChiefComplaints}
          onDurationChange={setComplaintsDuration}
        />
        
        {/* Clinical Details (Findings, Diagnosis, Lab Tests) */}
        <ClinicalDetails 
          clinicalFindings={clinicalFindings}
          diagnosis={diagnosis}
          labTests={labTests}
          onClinicalFindingsChange={setClinicalFindings}
          onDiagnosisChange={setDiagnosis}
          onLabTestsChange={setLabTests}
        />
        
        <Separator />
        
        {/* Medications Section */}
        <MedicationForm 
          medications={medications}
          onAddMedication={handleAddMedication}
          onAddMultipleMedications={handleAddMultipleMedications}
          onRemoveMedication={handleRemoveMedication}
          onUpdateMedication={handleUpdateMedication}
        />
        
        <AdditionalNotes notes={notes} setNotes={setNotes} />

        {/* Follow-up Section */}
        <div className="space-y-2">
          <Label htmlFor="followUp">Follow-up After (Days)</Label>
          <Input
            id="followUp"
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            value={followUpDays}
            onChange={(e) => {
              const value = e.target.value;
              // Only allow numeric input
              if (value === '' || /^\d+$/.test(value)) {
                setFollowUpDays(value === '' ? 0 : Number(value));
              }
            }}
            onBlur={(e) => {
              // Ensure value is at least 0 when leaving the field
              const value = Number(e.target.value) || 0;
              setFollowUpDays(Math.max(0, value));
            }}
            placeholder="0"
            className="w-32"
          />
        </div>
      </CardContent>
      <PrescriptionActions handleSavePrescription={handleSavePrescription} existingDraft={existingDraft} />
    </Card>
  );
}
